import clone from 'lodash/clone';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';

import * as ProposalNS from './Proposal';
import * as ProposalElementsNS from './ProposalElements';
import { shouldBuildElement, validateDisableRules } from './ProposalHelpers';

import { DAOManager } from './DAOs/DAOManager';
import {
  ProposalDAOInterface,
  DisclaimerConfig,
  ComparisonItemBasedProposalDAOInterface,
  ProposalTableDataDAOInterface,
  ProposalDAOInterfaces,
} from './DAOs/ProposalDAO';
import { deepClone } from '../../utils/objects';
import Dictionary from '@/types/dictionary';
import ComparisonItem from '@/types/comparison-item';
import User from '@/types/user';
import Nullable from '@/types/nullable';

const addDisclaimerKeys = (newDisclaimerConfigs: DisclaimerConfig[], disclaimerConfigs: DisclaimerConfig[]): void => {
  newDisclaimerConfigs.forEach(newDisclaimerConfig => {
    const existingConfig: DisclaimerConfig | undefined = disclaimerConfigs.find(
      disclaimerConfig => disclaimerConfig.key === newDisclaimerConfig.key && isEqual(disclaimerConfig.config, newDisclaimerConfig.config),
    );
    if (existingConfig === undefined) {
      disclaimerConfigs.push(newDisclaimerConfig);
    } else {
      newDisclaimerConfig.items.forEach(newItem => {
        if (existingConfig.items.find(item => item === newItem) === undefined) {
          existingConfig.items.push(newItem);
          existingConfig.items = uniqBy(existingConfig.items, '_id');
        }
      });
    }
  });
};

const validateItemIteratorConditions = (item: ComparisonItem, conditions: ProposalNS.ProposalItemIteratorCondition[]) => {
  for (const condition of conditions) {
    switch (condition.type) {
      case ProposalNS.ProposalItemIteratorConditionType.ItemType: {
        const types = condition.value as ProposalNS.ProposalAllowedItemType[];

        if (!types.includes(item.type)) {
          return false;
        }

        break;
      }
      case ProposalNS.ProposalItemIteratorConditionType.ItemRiskProfile: {
        if (condition.value && item.riskProfile === undefined) {
          return false;
        }

        break;
      }
      case ProposalNS.ProposalItemIteratorConditionType.ItemPurpose: {
        if (!item.flags?.includes(condition.value)) {
          return false;
        }

        break;
      }
      case ProposalNS.ProposalItemIteratorConditionType.ItemProperty: {
        if (get(item, condition.property) == null) {
          return false;
        }
        break;
      }
    }
  }
  return true;
};

const proposalTemplateDataToDataElement = (
  templateData: ProposalNS.ProposalTemplateDataElement,
  proposal: ProposalNS.Proposal,
  parent: Nullable<ProposalElementsNS.ProposalLayoutElement>,
  disclaimers: Nullable<DisclaimerConfig[]>,
  pageOverflowIndex: number,
  sectionIteratorIndex?: number,
) => {
  const data: ProposalElementsNS.ProposalDataElement = {
    ...templateData,
    pageOverflowIndex,
    parent: parent || undefined,
    elementType: ProposalElementsNS.ProposalElementType.Data,
  };

  const dao: ProposalDAOInterface = DAOManager.getInstance().buildDAO(templateData.DAO.type, proposal, data, sectionIteratorIndex);

  const compItemDAO: ComparisonItemBasedProposalDAOInterface | undefined = dao.getInterface<ComparisonItemBasedProposalDAOInterface>(
    ProposalDAOInterfaces.ComparisonItem,
  );
  if (disclaimers && compItemDAO !== undefined) {
    addDisclaimerKeys(compItemDAO.getDisclaimerConfigs(), disclaimers);
  }

  data.daoObject = dao;
  return data;
};

const buildDataElement = (
  templateData: ProposalNS.ProposalTemplateDataElement,
  parent: ProposalElementsNS.ProposalLayoutElement,
  proposal: ProposalNS.Proposal,
  disclaimers: DisclaimerConfig[],
  user: User,
  pageOverflowIndex: number,
  sectionIteratorIndex?: number,
): ProposalElementsNS.ProposalDataElement | null => {
  if (!shouldBuildElement(templateData, proposal, user, pageOverflowIndex)) {
    return null;
  }

  return proposalTemplateDataToDataElement(templateData, proposal, parent, disclaimers, pageOverflowIndex, sectionIteratorIndex);
};

const getChildDataElements = (element: ProposalNS.ProposalTemplateElement): ProposalNS.ProposalTemplateDataElement[] => {
  switch (element.type) {
    case ProposalNS.ProposalTemplateElementType.Layout: {
      return element.elements.reduce(
        (acc: ProposalNS.ProposalTemplateDataElement[], childElement: ProposalNS.ProposalTemplateElement) => acc.concat(getChildDataElements(childElement)),
        [],
      );
    }
    case ProposalNS.ProposalTemplateElementType.Data: {
      return [element];
    }
  }

  return [];
};

const getChildLayoutElements = (element: ProposalNS.ProposalTemplateLayoutElement, viewIndex: number): ProposalNS.ProposalTemplateLayoutElement[] => {
  let out: ProposalNS.ProposalTemplateLayoutElement[] = [];

  if (element.layout.type === ProposalNS.LayoutType.Switch) {
    if (viewIndex !== -1 && element.elements.length > viewIndex) {
      const selectedElement = element.elements[viewIndex];
      if (selectedElement.type === ProposalNS.ProposalTemplateElementType.Layout) {
        out.push(selectedElement);
        for (const childElement of selectedElement.elements) {
          if (childElement.type === ProposalNS.ProposalTemplateElementType.Layout) {
            out = out.concat(getChildLayoutElements(childElement as ProposalNS.ProposalTemplateLayoutElement, -1));
          }
        }
      }
    }
  } else {
    out.push(element);
    for (const childElement of element.elements) {
      if (childElement.type === ProposalNS.ProposalTemplateElementType.Layout) {
        out = out.concat(getChildLayoutElements(childElement as ProposalNS.ProposalTemplateLayoutElement, -1));
      }
    }
  }

  return out;
};

const buildLayoutElement = (
  templateLayout: ProposalNS.ProposalTemplateLayoutElement,
  parent: ProposalElementsNS.ProposalLayoutElement | ProposalElementsNS.ProposalSectionElement,
  viewTypes: Dictionary<string>,
  proposal: ProposalNS.Proposal,
  disclaimers: DisclaimerConfig[],
  user: User,
  pageOverflowIndex: number,
  sectionIteratorIndex?: number,
): ProposalElementsNS.ProposalLayoutElement | null => {
  if (!shouldBuildElement(templateLayout, proposal, user, pageOverflowIndex)) {
    return null;
  }

  if (templateLayout.layout.key !== undefined && templateLayout.layout.type === ProposalNS.LayoutType.Switch) {
    const viewKey = viewTypes[templateLayout.layout.key];
    if (viewKey !== undefined) {
      for (const element of templateLayout.elements) {
        if (element.type === ProposalNS.ProposalTemplateElementType.Layout && element.layout.key === viewKey) {
          return buildLayoutElement(
            element as ProposalNS.ProposalTemplateLayoutElement,
            parent,
            viewTypes,
            proposal,
            disclaimers,
            user,
            pageOverflowIndex,
            sectionIteratorIndex,
          );
        }
      }
    }

    return null;
  }

  let layouts: ProposalElementsNS.ProposalLayoutElement[] = [];
  const { itemIterator } = templateLayout.layout;
  let limit: number = 1;
  let startIndex: number = 0;

  if (itemIterator !== undefined) {
    limit = proposal.items.length;

    if (itemIterator.maxItemPerPage !== undefined) {
      startIndex = pageOverflowIndex * itemIterator.maxItemPerPage;
      limit = startIndex + itemIterator.maxItemPerPage;
      limit = Math.min(limit, proposal.items.length);
    }
  }

  for (let i: number = startIndex; i < limit; i++) {
    const copy: ProposalNS.ProposalTemplateLayoutElement = deepClone(templateLayout);
    const layout: ProposalElementsNS.ProposalLayoutElement = { ...copy, elementType: ProposalElementsNS.ProposalElementType.Layout };
    layout.parent = parent;
    layout.elements = [];

    if (itemIterator !== undefined) {
      if (!proposal.items[i] || (itemIterator.conditions && !validateItemIteratorConditions(proposal.items[i], itemIterator.conditions))) {
        continue; // eslint-disable-line no-continue
      }

      proposal.itemIterators.set(itemIterator.key, i);
      layout.layout.className = '';
    }

    for (const element of templateLayout.elements) {
      switch (element.type) {
        case ProposalNS.ProposalTemplateElementType.Layout: {
          const newElement: ProposalElementsNS.ProposalLayoutElement | null = buildLayoutElement(
            element,
            layout,
            viewTypes,
            proposal,
            disclaimers,
            user,
            pageOverflowIndex,
            sectionIteratorIndex,
          );
          if (newElement != null) {
            layout.elements.push(newElement);
          }
          break;
        }
        case ProposalNS.ProposalTemplateElementType.Data: {
          const newElement: ProposalElementsNS.ProposalDataElement | null = buildDataElement(
            element,
            layout,
            proposal,
            disclaimers,
            user,
            pageOverflowIndex,
            sectionIteratorIndex,
          );
          if (newElement != null) {
            layout.elements.push(newElement);
          }
          break;
        }
      }
    }
    layouts.push(layout);
  }

  if (layouts.length === 0) {
    return null;
  }

  if (itemIterator === undefined && layouts.length === 1) {
    return layouts[0];
  }

  if (itemIterator !== undefined) {
    const copy: ProposalNS.ProposalTemplateLayoutElement = deepClone(templateLayout);
    const layout: ProposalElementsNS.ProposalLayoutElement = {
      ...copy,
      elements: [],
      elementType: ProposalElementsNS.ProposalElementType.Layout,
    };

    if (itemIterator.padStart) {
      for (let i = 0; i < itemIterator.padStart; i++) {
        if (itemIterator.maxItemPerPage == null || layouts.length < itemIterator.maxItemPerPage) {
          layouts = [layout, ...layouts];
        }
      }
    }

    if (itemIterator.padEnd) {
      for (let i = 0; i < itemIterator.padEnd; i++) {
        if (itemIterator.maxItemPerPage == null || layouts.length < itemIterator.maxItemPerPage) {
          layouts.push(layout);
        }
      }
    }
  }

  const wrapperCopy: ProposalNS.ProposalTemplateLayoutElement = clone(templateLayout);
  const wrapperLayout: ProposalElementsNS.ProposalLayoutElement = { ...wrapperCopy, elementType: ProposalElementsNS.ProposalElementType.Layout };
  wrapperLayout.parent = parent;
  wrapperLayout.layout = clone(wrapperLayout.layout);
  if (itemIterator !== undefined && itemIterator.maxItemPerPage !== undefined) {
    wrapperLayout.layout.columns = itemIterator.partialGrid ? layouts.length : itemIterator.maxItemPerPage;
  } else {
    wrapperLayout.layout.columns = limit;
  }
  for (const l of layouts) {
    l.parent = wrapperLayout;
  }

  wrapperLayout.elements = layouts;

  return wrapperLayout;
};

const buildSectionElement = (
  templateSection: ProposalNS.ProposalTemplateSectionElement,
  proposal: ProposalNS.Proposal,
  selectedSections: string[],
  selectedViews: Dictionary<string>,
  pageOverflowIndex: number,
  user: User,
  sectionIteratorIndex?: number,
): ProposalElementsNS.ProposalSectionElement | null => {
  if (!shouldBuildElement(templateSection, proposal, user, pageOverflowIndex)) {
    return null;
  }

  if (
    templateSection.disableRules != null &&
    validateDisableRules(templateSection.disableRules, selectedSections, proposal.items, proposal.riskProfile).isDisabled
  ) {
    return null;
  }

  const disclaimers: DisclaimerConfig[] = [];

  const section: ProposalElementsNS.ProposalSectionElement = {
    ...clone(templateSection),
    elementType: ProposalElementsNS.ProposalElementType.Section,
    pageOverflowIndex,
    sectionKey: templateSection.key,
  };

  const newElement: ProposalElementsNS.ProposalLayoutElement | null = buildLayoutElement(
    templateSection.layout,
    section,
    selectedViews,
    proposal,
    disclaimers,
    user,
    pageOverflowIndex,
    sectionIteratorIndex,
  );

  if (newElement != null) {
    section.layout = newElement;
  }

  section.disclaimerConfigs = disclaimers;
  return section;
};

const getSectionRepetitionCount = (templateSection: ProposalNS.ProposalTemplateSectionElement, proposal: ProposalNS.Proposal, viewIndex: number): number => {
  // Repetition scenarios:
  // - There's a table that spreads over multiple pages
  // - Nothing else for now, but many other possible scenarios.

  let repetitionCount: number = 1;

  // look for overflowing tables
  const dataElements = getChildDataElements(templateSection.layout);
  const tableElements = dataElements.filter(element => element.dataView.type === ProposalNS.DataView.Table);
  for (const tableElement of tableElements) {
    const dataView = tableElement.dataView as ProposalNS.TableViewOptions;

    const { orientation } = proposal.options;
    const maxRows = (dataView.dynamicMaxRowsPerPage ? dataView.dynamicMaxRowsPerPage[orientation] : undefined) ?? dataView.maxRowsPerPage;

    if (maxRows) {
      const data = proposalTemplateDataToDataElement(tableElement, proposal, null, null, 0);
      const dao: ProposalDAOInterface = DAOManager.getInstance().buildDAO(tableElement.DAO.type, proposal, data);

      const localDAO: ProposalTableDataDAOInterface | undefined = dao as ProposalTableDataDAOInterface;

      if (localDAO !== undefined) {
        repetitionCount = localDAO.getTableData().rows.length / maxRows;
        // For now, we take the first one. Will this scale? We will adjust when needed
        break;
      }
    }
  }

  // look for overflowing layouts
  const layoutElements = getChildLayoutElements(templateSection.layout, viewIndex);
  for (const layoutElement of layoutElements) {
    if (layoutElement.layout.itemIterator !== undefined && layoutElement.layout.itemIterator.maxItemPerPage !== undefined) {
      repetitionCount = proposal.items.length / layoutElement.layout.itemIterator.maxItemPerPage;
      // For now, we take the first one. Will this scale? We will adjust when needed
      break;
    }
  }

  // By default, the section should only show once.
  return repetitionCount;
};

const applySectionOverflowRepetition = (
  repetitionCount: number,
  templateSection: ProposalNS.ProposalTemplateSectionElement,
  proposal: ProposalNS.Proposal,
  selectedSections: string[],
  selectedViews: Dictionary<string>,
  user: User,
): ProposalElementsNS.ProposalSectionElement[] => {
  const sections: ProposalElementsNS.ProposalSectionElement[] = [];

  for (let index: number = 0; index < repetitionCount; index++) {
    const section = buildSectionElement(templateSection, proposal, selectedSections, selectedViews, index, user);

    if (section != null) {
      section.key = `${section.key}_${index}`;
      sections.push(section);
    }
  }

  return sections;
};

const applySectionRepetition = (
  templateSection: ProposalNS.ProposalTemplateSectionElement,
  proposal: ProposalNS.Proposal,
  selectedSections: string[],
  selectedViews: Dictionary<string>,
  user: User,
  viewIndex: number,
): ProposalElementsNS.ProposalSectionElement[] => {
  let sections: ProposalElementsNS.ProposalSectionElement[] = [];
  const { itemIterator, comparisonIterator } = templateSection;

  if (comparisonIterator !== undefined && comparisonIterator.propertyToIterate) {
    const propertyToIterate = get(proposal, comparisonIterator.propertyToIterate);
    if (propertyToIterate && Array.isArray(propertyToIterate)) {
      for (let index = 0; index < propertyToIterate.length; index++) {
        const section = buildSectionElement(templateSection, proposal, selectedSections, selectedViews, 0, user, index);
        if (section !== null) {
          section.key = `${section.key}_${index}`;
          section.sectionIteratorIndex = index;
          sections.push(section);
        }
      }
    }
    return sections;
  }

  if (itemIterator !== undefined) {
    const { conditions } = itemIterator;

    for (let index = 0; index < proposal.items.length; index++) {
      if (!proposal.items[index] || (conditions && !validateItemIteratorConditions(proposal.items[index], conditions))) {
        continue; // eslint-disable-line no-continue
      }

      proposal.itemIterators.set(itemIterator.key, index);

      const repetitionCount = getSectionRepetitionCount(templateSection, proposal, viewIndex);

      if (repetitionCount === 1) {
        const section = buildSectionElement(templateSection, proposal, selectedSections, selectedViews, 0, user, index);

        if (section !== null) {
          section.key = `${section.key}_${index}`;
          section.sectionIteratorIndex = index;
          sections.push(section);
        }
      } else {
        sections = [...sections, ...applySectionOverflowRepetition(repetitionCount, templateSection, proposal, selectedSections, selectedViews, user)];
      }
    }

    return sections;
  }

  const repetitionCount = getSectionRepetitionCount(templateSection, proposal, viewIndex);
  sections = [...sections, ...applySectionOverflowRepetition(repetitionCount, templateSection, proposal, selectedSections, selectedViews, user)];

  return sections;
};

export const buildProposalSectionElements = (
  proposal: ProposalNS.Proposal,
  selectedSections: string[],
  selectedViews: Dictionary<string>,
  user: User,
  customOrder?: ProposalNS.ProposalTemplateSectionElement[],
): Array<ProposalElementsNS.ProposalSectionElement> => {
  const sections: Array<ProposalElementsNS.ProposalSectionElement> = new Array<ProposalElementsNS.ProposalSectionElement>();
  // If we have a custom order, we use that. Otherwise, we use the order from the template
  const rawSections = customOrder && customOrder.length > 0 ? customOrder : deepClone(proposal.template.root.map(division => division.sections).flat(1));

  for (const section of rawSections) {
    const isSelected =
      selectedSections.indexOf(section.key) > -1 || // is selected
      section.forced; // is forced

    const shouldBuild = shouldBuildElement(section, proposal, user);
    const { isDisabled } = validateDisableRules(section.disableRules || [], selectedSections, proposal.items, proposal.riskProfile);

    if (isSelected && shouldBuild && !isDisabled) {
      const viewIndex: number = 0;
      const newSections: ProposalElementsNS.ProposalSectionElement[] = applySectionRepetition(
        section,
        proposal,
        selectedSections,
        selectedViews,
        user,
        viewIndex,
      );
      for (let index = 0; index < newSections.length; index++) {
        sections.push(newSections[index]);
      }
    }
  }

  return sections;
};
